import React from 'react';
import {
  FileCheck,
  CheckCircle2,
  Layers,
  Network,
} from 'lucide-react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { StatCard } from '../../../../../components/ui/stat-card';
import { Skeleton } from '../../../../../components/ui/skeleton';
import type { ProcessingStats } from '../types';

interface StatsDashboardProps {
  stats: ProcessingStats | null;
  statsLoading: boolean;
}

export default function StatsDashboard({ stats, statsLoading }: StatsDashboardProps) {
  if (statsLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="border-2 border-gray-200">
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const failed = stats.failed || 0;
  const completed = stats.completed || 0;
  const processing = stats.processing || 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        title="Documents Processed"
        value={(stats.documentsProcessed || 0).toLocaleString()}
        icon={FileCheck}
        variant="info"
        description={
          processing > 0
            ? `${processing} in progress`
            : completed > 0
            ? `${completed} completed`
            : 'No documents yet'
        }
      />
      <StatCard
        title="Success Rate"
        value={`${stats.successRate || 0}%`}
        icon={CheckCircle2}
        variant={stats.successRate >= 80 ? 'success' : stats.successRate >= 50 ? 'warning' : 'default'}
        description={
          failed > 0 ? `${failed} failed` : completed > 0 ? `${completed} successful` : 'No results yet'
        }
      />
      <StatCard
        title="Frameworks Created"
        value={stats.frameworksCreated || 0}
        icon={Layers}
        variant="default"
        description={
          stats.templatesCreated
            ? `${stats.templatesCreated} template${stats.templatesCreated !== 1 ? 's' : ''} generated`
            : 'From parsed documents'
        }
      />
      <StatCard
        title="Nodes & Fields"
        value={(stats.totalNodes || 0).toLocaleString()}
        icon={Network}
        variant="warning"
        description={
          stats.totalFields
            ? `${(stats.totalFields || 0).toLocaleString()} fields across all templates`
            : 'Requirements extracted'
        }
      />
    </div>
  );
}
